import { PDFDocument, PDFFont, rgb } from "pdf-lib";
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ImageDimensions, RenderOptions } from "../types";

export const getXPosition = (width: number, margin: number = 5) =>
  (margin * width) / 100;

export const getYPosition = (height: number, margin: number = 5) =>
  (margin * height) / 100;

export const sanitizeAndConvertText = (text: string) => {
  // Remove HTML tags and scripts
  text = text.replace(/<[^>]*>/g, "");
  text = text.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    ""
  );
  // Remove newline characters (\n) and carriage return characters (\r)
  text = text.replace(/[\n\r]/g, "");
  // Remove other non-printable ASCII characters (codes 0 to 31 and 127)
  text = text.replace(/[\x00-\x1F\x7F]/g, "");

  // Convert to Unicode (UTF-8)
  return text;
};

export const wrapText = (
  text: string,
  maxWidth: number,
  fontSize: number,
  font: PDFFont
) => {
  const words = text.split(" ");
  const lines = [];
  let currentLine = "";

  for (const word of words) {
    const newLine = currentLine.trim() + (currentLine ? " " : "") + word;
    const lineWidth = font.widthOfTextAtSize(newLine, fontSize);

    if (lineWidth > maxWidth) {
      lines.push(currentLine.trim());
      currentLine = word;
    } else {
      currentLine = newLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine.trim());
  }

  return lines;
};

export const fetchImage = (url: string): Promise<ArrayBuffer> => {
  return new Promise<ArrayBuffer>(async (resolve, reject) => {
    try {
      console.log('============URL to fetch:', url, "=================");
      if (!url) {
        reject("URL is not sent to fetch image.");
      }

      if (url.includes("https") || url.includes("base64")) {
        console.log('============Downloading from url:', url, "=================");
        url = decodeURIComponent(url);

        await fetch(url)
          .then((res) => res.arrayBuffer())
          .then((res) => {
            resolve(res);
          });
      } else {
        console.log("===========Fetching file from s3 key==========");
        // Creating a s3 bucket client
        const s3Client = new S3Client({});

        // Bucketname
        let bucketName = "";

        console.log("========URL for s3 key: ", url, "=============");

        if (url.includes("===")) {
          console.log("===========URL is having s3 itself==========");
          let splits = url.split("===");
          bucketName = splits?.[1];
          url = splits?.[2];
        }

        console.log("========Bucket Name: ", bucketName, "=============");
        console.log("========URL for s3 : ", url, "=============");

        let res = await s3Client.send(
          new GetObjectCommand({ Bucket: bucketName, Key: url })
        );

        resolve((await res.Body.transformToByteArray()).buffer as ArrayBuffer);
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const fetchImageForJSPDF = (url: string): Promise<string> => {
  return new Promise<string>(async (resolve, reject) => {
    try {
      const fileType = url.split('.').at(-1).toLowerCase()?.split('?')?.[0] ?? "jpeg";
      console.log('============URL to fetch:', url, "=================");
      if (!url) {
        reject("URL is not sent to fetch image.");
      }

      if (url.includes("https")) {
        console.log('============Downloading from url:', url, "=================");
        const logoBytes = await fetchImage(url);
        resolve(`data:image/${fileType};base64,${arrayBufferToBase64(logoBytes)}`);
      } else if (url.includes('base64,')) {
        console.log('============Returning the base64:', url, "=================");
        resolve(url);
      } else {
        console.log("===========Fetching file from s3 key==========");
        // Creating a s3 bucket client
        const s3Client = new S3Client({});

        // Bucketname
        let bucketName = "";

        console.log("========URL for s3 key: ", url, "=============");

        if (url.includes("===")) {
          console.log("===========URL is having s3 itself==========");
          let splits = url.split("===");
          bucketName = splits?.[1];
          url = splits?.[2];
        }

        console.log("========Bucket Name: ", bucketName, "=============");
        console.log("========URL for s3 : ", url, "=============");

        let res = await s3Client.send(
          new GetObjectCommand({ Bucket: bucketName, Key: url })
        );

        // Convert the response to Base64
        const byteArray = await res.Body.transformToByteArray();
        const base64Image = Buffer.from(byteArray).toString("base64");

        // Assuming the image is a JPEG; adjust if needed.
        resolve(`data:image/${fileType};base64,${base64Image}`);
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const pxToInch = (px) => px * 0.2645833333;

export const convertNumberToCurrencyString = (
  number: number | bigint,
  currency: string = "USD"
): string => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });
  return formatter.format(number);
};

function createCurrencyFormatter(currencyCode) {
  const formatConfig: Intl.NumberFormatOptions = {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,  // Enable thousands grouping
    currencyDisplay: "code"
  };

  // Special cases for currencies that need different fraction digits
  const specialCases = {
    JPY: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
    KRW: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useCustom: true,
    },
    INR: { useCustom: true },
    ILS: { useCustom: true },
  };

  if (specialCases[currencyCode]) {
    Object.assign(formatConfig, specialCases[currencyCode]);
  }

  // Helper function to format large numbers with M/B
  const formatLargeNumber = (amount, decimalPlaces = 1) => {
    const absAmount = Math.abs(amount);
    if (absAmount >= 1e9) {
      return (absAmount / 1e9).toFixed(decimalPlaces) + "B";
    } else if (absAmount >= 1e6) {
      return (absAmount / 1e6).toFixed(decimalPlaces) + "M";
    }
    // Add commas for thousands
    return absAmount.toLocaleString('en-US', {
      minimumFractionDigits: formatConfig.minimumFractionDigits,
      maximumFractionDigits: formatConfig.maximumFractionDigits,
      useGrouping: true
    });
  };

  // Special handler for INR to ensure correct grouping
  if (currencyCode === "INR") {
    return (amount, useMB = false, showCurrency = true) => {
      if (useMB) {
        const formatted = formatLargeNumber(amount);
        return amount < 0 ? `(-) ${showCurrency ? 'INR ' : ''}${formatted}` : `${showCurrency ? 'INR ' : ''}${formatted}`;
      }

      const [wholePart, decimalPart = "00"] = Math.abs(amount)
        .toFixed(2)
        .split(".");
      let formattedWholePart = "";
      let count = 0;

      for (let i = wholePart.length - 1; i >= 0; i--) {
        if (count === 3 && formattedWholePart.length === 3) {
          formattedWholePart = "," + formattedWholePart;
          count = 0;
        } else if (count === 2 && formattedWholePart.length > 3) {
          formattedWholePart = "," + formattedWholePart;
          count = 0;
        }
        formattedWholePart = wholePart[i] + formattedWholePart;
        count++;
      }

      return amount < 0
        ? `(-) ${showCurrency ? 'INR ' : ''}${formattedWholePart}.${decimalPart}`
        : `${showCurrency ? 'INR ' : ''}${formattedWholePart}.${decimalPart}`;
    };
  }

  // Special handler for KRW to ensure correct formatting
  if (currencyCode === "KRW") {
    return (amount, useMB = false, showCurrency = true) => {
      if (useMB) {
        const formatted = formatLargeNumber(amount);
        return amount < 0 ? `(-) ${showCurrency ? 'KRW ' : ''}${formatted}` : `${showCurrency ? 'KRW ' : ''}${formatted}`;
      }

      const wholePart = Math.round(Math.abs(amount));
      let formattedWholePart = wholePart.toLocaleString('en-US');
      return amount < 0 ? `(-) ${showCurrency ? 'KRW ' : ''}${formattedWholePart}` : `${showCurrency ? 'KRW ' : ''}${formattedWholePart}`;
    };
  }

  // Special handler for ILS to ensure correct formatting
  if (currencyCode === "ILS") {
    return (amount, useMB = false, showCurrency = true) => {
      if (useMB) {
        const formatted = formatLargeNumber(amount);
        return amount < 0 ? `(-) ${showCurrency ? 'ILS ' : ''}${formatted}` : `${showCurrency ? 'ILS ' : ''}${formatted}`;
      }

      const formattedNumber = Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
      });
      return amount < 0
        ? `(-) ${showCurrency ? 'ILS ' : ''}${formattedNumber}`
        : `${showCurrency ? 'ILS ' : ''}${formattedNumber}`;
    };
  }

  try {
    const formatter = new Intl.NumberFormat("en-US", formatConfig);
    return (amount, useMB = false, showCurrency = true) => {
      if (useMB) {
        const formatted = formatLargeNumber(amount);
        // const standardFormatted = formatter.format(Math.abs(1)).replace(/[0-9.,]/g, '').trim();
        // const currencySymbol = standardFormatted || currencyCode;
        return amount < 0
          ? `(-) ${showCurrency ? `${currencyCode} ` : ''}${formatted}`
          : `${showCurrency ? `${currencyCode} ` : ''}${formatted}`;
      }
      let formattedCurrency = formatter.format(amount);
      if (!showCurrency) {
        formattedCurrency = formattedCurrency.replace(currencyCode, "").trim()
      }
      return formattedCurrency;
    };
  } catch (error) {
    // Fallback formatter if Intl.NumberFormat fails
    return (amount, useMB = false, showCurrency = true) => {
      if (useMB) {
        const formatted = formatLargeNumber(amount);
        return amount < 0
          ? `(-) ${showCurrency ? `${currencyCode} ` : ''}${formatted}`
          : `${showCurrency ? `${currencyCode} ` : ''}${formatted}`;
      }

      const formatted = Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: formatConfig.minimumFractionDigits,
        maximumFractionDigits: formatConfig.maximumFractionDigits,
        useGrouping: true
      });
      return amount < 0
        ? `(-) ${showCurrency ? `${currencyCode} ` : ''}${formatted}`
        : `${showCurrency ? `${currencyCode} ` : ''}${formatted}`;
    };
  }
}

// Usage function
export function formatCurrency(amount, currencyCode, useMB = true, showCurrency = true) {
  const formatter = createCurrencyFormatter(currencyCode);
  return formatter(amount, useMB, showCurrency);
}

export const getImageBase64 = async (url: string): Promise<string | ArrayBuffer> => {
  const response = await fetch(url);
  const blob = await response.blob();

  // Convert blob to base64
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// To convert color codes to grey scale values.
export const hexToGrayscale = (color: string = "rgb(230, 229, 229)"): number => {
  let red: number, green: number, blue: number;

  if (color?.startsWith("#")) {
    // Remove the '#' symbol if present
    color = color.replace(/^#/, "");

    // Parse the RGB components from the hex color
    red = parseInt(color.substr(0, 2), 16);
    green = parseInt(color.substr(2, 2), 16);
    blue = parseInt(color.substr(4, 2), 16);
  } else if (color?.startsWith("rgb")) {
    // Extract RGB components from the rgb(...) string
    const match = color.match(/\d+/g);
    if (!match || match.length < 3) {
      return 160
    }
    red = parseInt(match[0], 10);
    green = parseInt(match[1], 10);
    blue = parseInt(match[2], 10);
  } else {
    return 160
  }

  // Calculate grayscale using perceived brightness formula
  const grayscale = Math.floor(0.299 * red + 0.587 * green + 0.114 * blue);

  return grayscale;
};

// Function to add a hyperlink to text in a PDF
export async function addHyperlinkToPdf(pdfDoc, page, text, url, x, y, font, fontSize = 12, color = rgb(0.5, 0.5, 0.5)) {
  // Add the text first
  await page.drawText(text, {
    x,
    y,
    size: fontSize,
    color,
    underline: true
  });

  const textWidth = font.widthOfTextAtSize(text, fontSize);

  // Create a link annotation
  return pdfDoc.context.register(
    pdfDoc.context.obj({
      Type: 'Annot',
      Subtype: 'Link',
      Rect: [x, y - 2, x + textWidth, y + fontSize],
      Border: [0, 0, 0],
      C: [0, 0, 0],
      A: {
        Type: 'Action',
        S: 'URI',  // URI action
        URI: url
      }
    })
  );
}

// Chart Rendering Functions
export const embedChartInJSPDF = async (
  doc: jsPDF,
  id: string,
  xPos: number,
  yPos: number,
  width = 0,
  height = 0,
  options: RenderOptions = {}
): Promise<number> => {
  const {
    maxWidthPercentage = 90,
    maxHeightPercentage = 80,
    preserveAspectRatio = true,
    scale = 2,
    format = 'PNG',
    quality = 1
  } = options;

  // Get the chart element
  const chart = document?.getElementById(id);
  if (!chart) {
    console.warn(`Chart element with id '${id}' not found`);
    return 0;
  }

  // Calculate page dimensions based on orientation
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Calculate maximum allowed dimensions
  const maxWidth = (pageWidth * maxWidthPercentage) / 100;
  const maxHeight = (pageHeight * maxHeightPercentage) / 100;

  try {
    // Create high-resolution canvas
    const canvas = await html2canvas(chart, {
      scale,
      logging: false,
      useCORS: true
    });

    // Get image data with specified format and quality
    const imgData = canvas.toDataURL(`image/${format.toLowerCase()}`, quality);

    // Calculate dimensions while preserving aspect ratio if needed
    const dimensions = calculateDimensions(
      canvas.width,
      canvas.height,
      {
        maxWidth,
        maxHeight,
        width,
        height,
        preserveAspectRatio
      }
    );

    // Check if image will fit on current page
    if (yPos + dimensions.height > pageHeight) {
      doc.addPage();
      yPos = 13; // Reset Y position on new page
    }

    // Center image horizontally if smaller than page width
    const xPosition = width === 0 ?
      (pageWidth - dimensions.width) / 2 :
      xPos;

    // Add image to PDF
    doc.addImage(
      imgData,
      format,
      xPosition,
      yPos,
      dimensions.width,
      dimensions.height
    );

    return yPos + dimensions.height;
  } catch (error) {
    console.error('Error rendering chart to PDF:', error);
    return yPos;
  }
};

// Helper function to calculate image dimensions
const calculateDimensions = (
  originalWidth: number,
  originalHeight: number,
  params: {
    maxWidth: number,
    maxHeight: number,
    width: number,
    height: number,
    preserveAspectRatio: boolean
  }
): ImageDimensions => {
  const { maxWidth, maxHeight, width, height, preserveAspectRatio } = params;

  // If specific dimensions are provided and aspect ratio isn't important
  if (width && height && !preserveAspectRatio) {
    return { width, height };
  }

  // Calculate aspect ratio
  const aspectRatio = originalWidth / originalHeight;

  // If specific width is provided
  if (width) {
    const calculatedHeight = preserveAspectRatio ?
      width / aspectRatio :
      height || maxHeight;
    return {
      width,
      height: Math.min(calculatedHeight, maxHeight)
    };
  }

  // If specific height is provided
  if (height) {
    const calculatedWidth = preserveAspectRatio ?
      height * aspectRatio :
      width || maxWidth;
    return {
      width: Math.min(calculatedWidth, maxWidth),
      height
    };
  }

  // Calculate dimensions to fit within maximum bounds
  let finalWidth = originalWidth;
  let finalHeight = originalHeight;

  // Scale down if width exceeds maximum
  if (finalWidth > maxWidth) {
    finalWidth = maxWidth;
    finalHeight = preserveAspectRatio ?
      finalWidth / aspectRatio :
      Math.min(finalHeight, maxHeight);
  }

  // Scale down if height exceeds maximum
  if (finalHeight > maxHeight) {
    finalHeight = maxHeight;
    finalWidth = preserveAspectRatio ?
      finalHeight * aspectRatio :
      Math.min(finalWidth, maxWidth);
  }

  return { width: finalWidth, height: finalHeight };
};

/**
 * Convert ArrayBuffer to Base64 string.
 * @param buffer - The ArrayBuffer containing image data.
 * @returns Base64 encoded string.
 */
export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
}

export const embedPDF = async (Document: typeof PDFDocument, pdfDoc: PDFDocument, url: string) => {
  // Extract the Base64 part from the Data URL
  const base64String = url.split(",")[1]; // Remove "data:application/pdf;base64,"

  // Decode Base64 to byte array
  const pdfBytes = Uint8Array.from(atob(base64String), c => c.charCodeAt(0));

  // Load PDF
  const pdf = await Document.load(pdfBytes);

  let pages = pdf.getPages();

  // Embed pages into another PDF document
  const embeddedPages = await pdfDoc.embedPages(pages);

  embeddedPages.forEach(async (embeddedPage) => {
    const newPage = pdfDoc.addPage(); // Create a new page
    const { width, height } = embeddedPage;
    await newPage.drawPage(embeddedPage, { x: 0, y: 0, width, height }); // Draw the embedded page
  })
}
